import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, IconButton, type SxProps, type Theme, useMediaQuery, useTheme } from "@mui/material";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { type CSSProperties, type FC, type ReactNode, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { MOTION, NEUTRAL, SHADOW, SURFACE } from "../../../../theme/tokens";
import { CommonHeader } from "../../molecules/CommonHeader";
import { TOUCH_TARGET_SX } from "../../touchTarget";

type Props = {
	open: boolean;
	onClose: () => void;
	title: ReactNode;
	/** Scrollable body. */
	children: ReactNode;
	/** Sticky footer (e.g. a composer). */
	footer?: ReactNode;
	/** Extra content next to the header's close button. */
	headerEndingContent?: ReactNode;
	/** Collapsible leading rail (e.g. a sessions/conversations list). */
	sidebar?: ReactNode;
	/** Whether the sidebar rail is expanded. Default false. */
	sidebarOpen?: boolean;
	/** Collapse the sidebar. When provided, a back button shows in the fullscreen (mobile) sidebar overlay. */
	onSidebarClose?: () => void;
	/** Accessible label for the fullscreen sidebar's back button (caller supplies a translated string). */
	sidebarBackAriaLabel?: string;
	/** Expanded sidebar width (px or CSS). Default "40%". */
	sidebarWidth?: number | string;
	/** Window max width in px; narrower viewports get 100vw minus margins. Default 600. */
	maxWidth?: number;
	/** Window height CSS. Default caps at 650px and always clears the viewport. */
	height?: string;
	/** Fixed offsets (px) from the viewport corner — defaults clear a bottom-right launcher FAB. */
	bottom?: number;
	right?: number;
	zIndex?: number;
	/** Accessible name for the dialog (icon/complex titles won't self-label). */
	ariaLabel?: string;
	/** Force fullscreen layout. Defaults to the `sm` breakpoint; callers pass their own "is mobile" so
	 * the window's fullscreen decision matches how the feature gates mounting. */
	fullscreen?: boolean;
	/** Extra sx on the scrollable body. */
	bodySx?: SxProps<Theme>;
	dataTestId?: string;
};

// Geometry defaults for the floating window. These are the component's tunable knobs —
// every one is also exposed as a prop, so a call site overrides per instance (that IS the
// design-system dial for this component; see AnniePanel). The bottom/right defaults agree
// with ChatLauncherFab's so the window appears to rise out of the launcher FAB.
const DEFAULT_MAX_WIDTH = 600;
const DEFAULT_HEIGHT = "min(650px, calc(100vh - 160px))";
const DEFAULT_BOTTOM = 100;
const DEFAULT_RIGHT = 50;
const DEFAULT_Z_INDEX = 1300;
const DEFAULT_SIDEBAR_WIDTH = "40%";
/** Combined horizontal viewport margin (px) when narrower than maxWidth. */
const VIEWPORT_MARGIN = 40;

// Motion is owned by the MOTION tokens. Framer Motion takes seconds; the tokens are authored in ms.
const ENTER_DURATION_S = MOTION.DURATION.WINDOW_ENTER / 1000;
const EXIT_DURATION_S = MOTION.DURATION.WINDOW_EXIT / 1000;
// Framer-motion tuple form of MOTION.EASE_OUT ("cubic-bezier(0.23, 1, 0.32, 1)") — the same curve,
// which Framer expects as a [x1, y1, x2, y2] array rather than the CSS string the token holds.
const EASE_OUT: [number, number, number, number] = [0.23, 1, 0.32, 1];

/**
 * Generic floating non-modal chat/utility window anchored above a bottom-right
 * launcher: compact fixed-size surface (never a full-height drawer), the page
 * behind stays interactive (no scrim), fullscreen on the sm breakpoint. Sticky
 * CommonHeader + scrollable body + optional sticky footer + optional collapsible
 * leading sidebar rail. GPU-only scale/fade enter-exit honoring reduced-motion;
 * Escape-to-close; keyboard focus moves in on open.
 */
export const FloatingChatWindow: FC<Props> = ({
	open,
	onClose,
	title,
	children,
	footer,
	headerEndingContent,
	sidebar,
	sidebarOpen = false,
	onSidebarClose,
	sidebarBackAriaLabel,
	sidebarWidth = DEFAULT_SIDEBAR_WIDTH,
	maxWidth = DEFAULT_MAX_WIDTH,
	height = DEFAULT_HEIGHT,
	bottom = DEFAULT_BOTTOM,
	right = DEFAULT_RIGHT,
	zIndex = DEFAULT_Z_INDEX,
	ariaLabel,
	fullscreen,
	bodySx,
	dataTestId,
}) => {
	const reduceMotion = useReducedMotion();
	const theme = useTheme();
	const smFullscreen = useMediaQuery(theme.breakpoints.down("sm"));
	// Caller's explicit "is mobile" wins so the window matches the feature's mount-gating breakpoint;
	// falls back to the sm breakpoint when unspecified.
	const isFullscreen = fullscreen ?? smFullscreen;
	const windowRef = useRef<HTMLElement | null>(null);

	useEffect(() => {
		if (!open) return;
		const handleKeyDown = (event: KeyboardEvent) => {
			// Only a real key press: a tour closes the surfaces it opened by dispatching Escape.
			if (event.key === "Escape" && event.isTrusted) onClose();
		};
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [open, onClose]);

	// Dialog a11y: move keyboard focus into the window on open — unless a child
	// (e.g. an autoFocus input) already claimed it during the same mount.
	useEffect(() => {
		if (!open) return;
		const frame = requestAnimationFrame(() => {
			const windowEl = windowRef.current;
			if (windowEl && !windowEl.contains(document.activeElement)) windowEl.focus();
		});
		return () => cancelAnimationFrame(frame);
	}, [open]);

	// Reduced motion keeps the fade but drops the scale/lift.
	const hiddenState = reduceMotion ? { opacity: 0 } : { opacity: 0, transform: "translateY(12px) scale(0.96)" };
	const visibleState = reduceMotion ? { opacity: 1 } : { opacity: 1, transform: "translateY(0px) scale(1)" };

	// Fullscreen (mobile): `100dvh` rather than `inset: 0` + `height: 100%` — that combination
	// over-constrains the box, so `bottom` is dropped and the height resolves against the LARGE viewport,
	// putting the sticky composer below the fold behind mobile browser chrome.
	//
	// No safe-area padding: `index.html` does not set `viewport-fit=cover`, so every `env(safe-area-inset-*)`
	// resolves to 0 and the browser/WebView has already inset the viewport itself. Opting into cover would
	// push the WHOLE app under the notch, so it belongs in its own reviewed change, not here.
	const placementStyle: CSSProperties = isFullscreen
		? {
				top: 0,
				left: 0,
				right: 0,
				height: "100dvh",
				borderRadius: 0,
				boxSizing: "border-box",
			}
		: {
				bottom,
				right,
				width: `calc(100vw - ${VIEWPORT_MARGIN}px)`,
				maxWidth,
				height,
				borderRadius: theme.spacing(2),
			};

	const windowTree = (
		<AnimatePresence>
			{open && (
				<motion.section
					ref={windowRef}
					role="dialog"
					aria-label={ariaLabel}
					tabIndex={-1}
					data-testid={dataTestId}
					initial={hiddenState}
					animate={visibleState}
					exit={{ ...hiddenState, transition: { duration: EXIT_DURATION_S, ease: EASE_OUT } }}
					transition={{ duration: ENTER_DURATION_S, ease: EASE_OUT }}
					style={{
						position: "fixed",
						zIndex,
						display: "flex",
						overflow: "hidden",
						backgroundColor: SURFACE,
						border: `1px solid ${NEUTRAL[200]}`,
						boxShadow: SHADOW.FLOATING_WINDOW,
						transformOrigin: "bottom right",
						...placementStyle,
					}}
				>
					{sidebar && (
						<Box
							sx={{
								flexShrink: 0,
								overflow: "hidden",
								display: "flex",
								flexDirection: "column",
								backgroundColor: NEUTRAL[100],
								// A 40% inline rail is too cramped on a phone — on mobile the rail becomes a full-width
								// slide-over that fully replaces the chat (dismiss via the back button). Desktop keeps
								// the inline push rail.
								...(isFullscreen
									? {
											position: "absolute",
											inset: 0,
											zIndex: 1,
											width: "100%",
											transform: sidebarOpen ? "translateX(0)" : "translateX(-100%)",
											transition: reduceMotion ? "none" : `transform ${MOTION.DURATION.ENTRANCE_MD}ms ${MOTION.EASE_OUT}`,
										}
									: {
											width: sidebarOpen ? sidebarWidth : 0,
											borderRight: sidebarOpen ? `1px solid ${NEUTRAL[200]}` : "none",
											transition: reduceMotion ? "none" : `width ${MOTION.DURATION.ENTRANCE_MD}ms ${MOTION.EASE_OUT}`,
										}),
							}}
						>
							{isFullscreen && onSidebarClose && (
								<Box sx={{ display: "flex", alignItems: "center", px: 1, pt: 1, flexShrink: 0 }}>
									<IconButton
										aria-label={sidebarBackAriaLabel}
										data-testid={dataTestId ? `${dataTestId}-sidebar-back` : undefined}
										onClick={onSidebarClose}
										size="small"
										sx={TOUCH_TARGET_SX}
									>
										<ArrowBackIcon fontSize="small" />
									</IconButton>
								</Box>
							)}
							{/* Bounds the rail so a `height: 100%` child resolves against the space left under the
							    back-button row, not the full sheet — otherwise its last rows are clipped. */}
							<Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>{sidebar}</Box>
						</Box>
					)}
					<Box sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
						<Box sx={{ px: 2, pt: 1.5, flexShrink: 0 }}>
							<CommonHeader title={title} onClose={onClose} endingContent={headerEndingContent} />
						</Box>
						<Box sx={[{ flex: 1, minHeight: 0, overflowY: "auto", p: 2 }, ...(Array.isArray(bodySx) ? bodySx : [bodySx])]}>{children}</Box>
						{footer && <Box sx={{ flexShrink: 0, p: 1.5, borderTop: `1px solid ${NEUTRAL[200]}`, backgroundColor: SURFACE }}>{footer}</Box>}
					</Box>
				</motion.section>
			)}
		</AnimatePresence>
	);

	// Rendered on `body` in BOTH layouts. Its `position: fixed` and z-index only rank against its nearest stacking
	// context, and a sticky or transformed ancestor at the call site (the layout's page column is sticky) trapped a
	// fullscreen window UNDER chrome that lives on `body` — a phone bottom bar covered the composer. And the portal must
	// not depend on the layout: switching between a portal and an in-place tree remounts the window, so rotating a phone
	// across the mobile threshold threw away everything in it (picks, a draft, the scroll, focus) — the loss the
	// caller's single mount exists to prevent. The floating window is fixed too, so on `body` it sits where it always
	// did. React context still flows through a portal.
	return createPortal(windowTree, document.body);
};
